import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { ModuleId } from "../auth/config.ts";
import type { WalkScope } from "../export/plan.ts";
import { decideItem } from "./item-version.ts";

/**
 * IndexedDB — all run state (ARCHITECTURE.md §7).
 *
 * This is what makes resume exact. An item is recorded complete *before* the
 * run moves on, so an interruption costs at most one in-flight item. Nothing
 * here holds exported content; content goes straight to disk.
 */

export type RunStatus = "pending" | "running" | "paused" | "completed" | "failed";
export type ItemStatus = "pending" | "done" | "failed" | "skipped";
export type LogLevel = "info" | "warn" | "error";

export interface RunRecord {
    runId: string;
    startedAt: string;
    modules: ModuleId[];
    options: Record<string, unknown>;
    status: RunStatus;
}

/**
 * One cursor per resource within a module — a chat, a mail folder, a calendar —
 * not one per module. Teams pages each chat's messages independently, and mail
 * and calendar will later need a deltaLink per folder/calendar rather than one
 * for the whole module, so the container is part of the key from the start.
 * Modules with only one resource use a constant subKey (see SINGLE_CURSOR).
 */
export interface CursorRecord {
    runId: string;
    module: ModuleId;
    subKey: string;
    /** Delta queries where the endpoint supports them, paging link otherwise. */
    deltaLink?: string;
    nextLink?: string;
    lastId?: string;
    done: boolean;
    /**
     * For containers with no delta endpoint (Teams chat messages, §8): the
     * container's own last-modified timestamp at the moment `done` was last
     * confirmed true. A later run compares this against the container's
     * current timestamp — if Graph itself reports nothing has changed, the
     * container is skipped without a single request; if it reports a change,
     * the container is re-scanned. This is what lets a finished chat still
     * pick up messages that arrived afterward, without guessing at the
     * messages endpoint's ordering.
     */
    completedAtSourceUpdate?: string;
    /**
     * The filters the walk behind this cursor applied (see WalkScope in
     * export/plan.ts). A later run continues from this cursor only if its own
     * filters are covered by these; otherwise the container is re-listed.
     */
    scope?: WalkScope;
}

/** subKey for modules that only ever have one cursor. */
export const SINGLE_CURSOR = "_";

export interface ItemRecord {
    runId: string;
    module: ModuleId;
    itemId: string;
    status: ItemStatus;
    bytes: number;
    error?: string;
    /**
     * Graph's change marker for the item when it was written (see
     * item-version.ts). Absent on records from before versions were kept, and
     * on items whose module does not track changes (Mail: a message's MIME
     * never changes).
     */
    version?: string;
}

/**
 * Directory handles. §7 keys these by runId; `CURRENT_HANDLE_KEY` is the
 * selection made before any run exists, which is what step 2 needs.
 */
export interface HandleRecord {
    key: string;
    dirHandle: FileSystemDirectoryHandle;
}

export const CURRENT_HANDLE_KEY = "current";

export interface MailboxRecord {
    address: string;
    validatedAt: string;
}

/**
 * One record per file written to the destination folder, keyed by path.
 *
 * Keyed by path rather than appended to a running counter so that rewriting a
 * file replaces its recorded size instead of adding to it — otherwise the
 * reported total drifts above what is actually on disk.
 */
export interface WrittenRecord {
    path: string;
    bytes: number;
}

export interface LogRecord {
    id?: number;
    runId: string;
    ts: string;
    level: LogLevel;
    module: ModuleId | "app";
    message: string;
}

interface ExporterDB extends DBSchema {
    runs: {
        key: string;
        value: RunRecord;
    };
    cursors: {
        key: [string, string, string];
        value: CursorRecord;
    };
    items: {
        key: [string, string, string];
        value: ItemRecord;
        indexes: { "by-run-module": [string, string] };
    };
    handles: {
        key: string;
        value: HandleRecord;
    };
    mailboxes: {
        key: string;
        value: MailboxRecord;
    };
    written: {
        key: string;
        value: WrittenRecord;
    };
    log: {
        key: number;
        value: LogRecord;
        indexes: { "by-run": string };
    };
}

const DB_NAME = "o365-exporter";
const DB_VERSION = 3;

let dbPromise: Promise<IDBPDatabase<ExporterDB>> | null = null;

export function db(): Promise<IDBPDatabase<ExporterDB>> {
    dbPromise ??= openDB<ExporterDB>(DB_NAME, DB_VERSION, {
        // Version-guarded and additive. Existing installs already hold a
        // directory handle, and recreating a store that exists throws — so each
        // version's changes must be applied only when upgrading past it.
        upgrade(database, oldVersion) {
            if (oldVersion < 1) {
                database.createObjectStore("runs", { keyPath: "runId" });
                database.createObjectStore("cursors", {
                    keyPath: ["runId", "module", "subKey"]
                });

                const items = database.createObjectStore("items", {
                    keyPath: ["runId", "module", "itemId"]
                });
                items.createIndex("by-run-module", ["runId", "module"]);

                database.createObjectStore("handles", { keyPath: "key" });
                database.createObjectStore("mailboxes", { keyPath: "address" });

                const log = database.createObjectStore("log", {
                    keyPath: "id",
                    autoIncrement: true
                });
                log.createIndex("by-run", "runId");
            }

            if (oldVersion < 2) {
                database.createObjectStore("written", { keyPath: "path" });
            }

            if (oldVersion < 3) {
                // The cursors keyPath changed from [runId, module] to
                // [runId, module, subKey], which IndexedDB cannot alter in
                // place. Nothing in this store is a loss to drop — a missing
                // cursor only means the next run re-fetches that resource from
                // the start rather than resuming it, which is safe, just slower.
                database.deleteObjectStore("cursors");
                database.createObjectStore("cursors", {
                    keyPath: ["runId", "module", "subKey"]
                });
            }
        },

        /*
         * Multi-tab upgrade handling. A long export is exactly the situation
         * where a second tab gets opened, and without these an upgrade blocks
         * forever with no visible cause.
         */

        // Another tab is holding an older version open, so our upgrade cannot
        // proceed until it closes or reloads.
        blocked() {
            console.warn(
                "IndexedDB upgrade is blocked by another tab running an older version."
            );
        },

        // We are the older tab standing in the way. Close so the other one can
        // upgrade; the next db() call reopens at the new version.
        blocking(_currentVersion, _blockedVersion, event) {
            (event.target as IDBDatabase | null)?.close();
            dbPromise = null;
        }
    });
    return dbPromise;
}

/* Handles ------------------------------------------------------------------ */

export async function saveDirHandle(
    handle: FileSystemDirectoryHandle,
    key: string = CURRENT_HANDLE_KEY
): Promise<void> {
    // FileSystemDirectoryHandle is structured-cloneable, so it survives in IDB
    // across sessions — the user is not asked to re-pick the folder (§7).
    await (await db()).put("handles", { key, dirHandle: handle });
}

export async function loadDirHandle(
    key: string = CURRENT_HANDLE_KEY
): Promise<FileSystemDirectoryHandle | null> {
    const record = await (await db()).get("handles", key);
    return record?.dirHandle ?? null;
}

export async function clearDirHandle(key: string = CURRENT_HANDLE_KEY): Promise<void> {
    await (await db()).delete("handles", key);
}

/* Runs --------------------------------------------------------------------- */

export async function putRun(run: RunRecord): Promise<void> {
    await (await db()).put("runs", run);
}

export async function getRun(runId: string): Promise<RunRecord | undefined> {
    return (await db()).get("runs", runId);
}

/* Cursors ------------------------------------------------------------------ */

export async function putCursor(cursor: CursorRecord): Promise<void> {
    await (await db()).put("cursors", cursor);
}

export async function getCursor(
    runId: string,
    module: ModuleId,
    subKey: string = SINGLE_CURSOR
): Promise<CursorRecord | undefined> {
    return (await db()).get("cursors", [runId, module, subKey]);
}

/* Items -------------------------------------------------------------------- */

/** Record an item's outcome. Call before moving to the next item, never after. */
export async function putItem(item: ItemRecord): Promise<void> {
    await (await db()).put("items", item);
}

export async function getItem(
    runId: string,
    module: ModuleId,
    itemId: string
): Promise<ItemRecord | undefined> {
    return (await db()).get("items", [runId, module, itemId]);
}

/**
 * The resume check with change detection: true when the item must be exported
 * now — never written, or written at a different `version` than Graph reports
 * today (see item-version.ts). A record from before versions were kept is
 * stamped with `version` on the way past instead of being re-exported.
 */
export async function itemNeedsExport(
    runId: string,
    module: ModuleId,
    itemId: string,
    version: string | undefined
): Promise<boolean> {
    const record = await getItem(runId, module, itemId);
    const decision = decideItem(record, version);
    if (decision === "adopt" && record) {
        await putItem({ ...record, ...(version !== undefined ? { version } : {}) });
        return false;
    }
    return decision === "export";
}

/** True when this item is already on disk — the resume check. */
export async function isItemDone(
    runId: string,
    module: ModuleId,
    itemId: string
): Promise<boolean> {
    return (await getItem(runId, module, itemId))?.status === "done";
}

export async function itemsForModule(
    runId: string,
    module: ModuleId
): Promise<ItemRecord[]> {
    return (await db()).getAllFromIndex("items", "by-run-module", [runId, module]);
}

export interface ModuleProgress {
    done: number;
    failed: number;
    skipped: number;
    bytes: number;
    /** Resources (chats, folders) whose cursor is marked fully complete. */
    containersComplete: number;
    containersSeen: number;
}

/**
 * Reconstructs a module's progress from what was recorded on the way through.
 *
 * This is what makes progress survive a reload: the items and cursors stores
 * are written as each piece lands on disk (§7), so they already describe
 * exactly what exists in the destination folder. Counted with a cursor rather
 * than getAll() so a run with hundreds of thousands of items is never
 * materialised in memory just to display a number.
 */
export async function moduleProgress(
    runId: string,
    module: ModuleId
): Promise<ModuleProgress> {
    const database = await db();

    let done = 0;
    let failed = 0;
    let skipped = 0;
    let bytes = 0;

    const index = database.transaction("items").store.index("by-run-module");
    let itemCursor = await index.openCursor(IDBKeyRange.only([runId, module]));
    while (itemCursor) {
        const record = itemCursor.value;
        if (record.status === "done") {
            done += 1;
            bytes += record.bytes;
        } else if (record.status === "failed") {
            failed += 1;
        } else if (record.status === "skipped") {
            skipped += 1;
        }
        itemCursor = await itemCursor.continue();
    }

    let containersComplete = 0;
    let containersSeen = 0;

    // Compound keys are ordered, so a bounded range over
    // [runId, module, *] selects exactly this module's cursors.
    const range = IDBKeyRange.bound([runId, module, ""], [runId, module, "￿"]);
    let cursorRecord = await database.transaction("cursors").store.openCursor(range);
    while (cursorRecord) {
        containersSeen += 1;
        if (cursorRecord.value.done) {
            containersComplete += 1;
        }
        cursorRecord = await cursorRecord.continue();
    }

    return { done, failed, skipped, bytes, containersComplete, containersSeen };
}

/* Mailboxes ---------------------------------------------------------------- */

export async function saveMailbox(address: string): Promise<void> {
    await (await db()).put("mailboxes", {
        address: address.toLowerCase(),
        validatedAt: new Date().toISOString()
    });
}

export async function listMailboxes(): Promise<MailboxRecord[]> {
    return (await db()).getAll("mailboxes");
}

export async function removeMailbox(address: string): Promise<void> {
    await (await db()).delete("mailboxes", address.toLowerCase());
}

/* Written files ------------------------------------------------------------ */

/** Records a file's size on disk. Overwriting a path replaces its entry. */
export async function recordWritten(path: string, bytes: number): Promise<void> {
    await (await db()).put("written", { path, bytes });
}

/**
 * Total bytes written to the current destination folder.
 *
 * Summed with a cursor rather than getAll() so the whole set is never
 * materialised in memory. Still linear in file count — once exports reach the
 * hundreds of thousands of items (§7 sizes mail at ~100k), this wants replacing
 * with an incrementally maintained aggregate.
 */
export async function totalBytesWritten(): Promise<number> {
    let total = 0;
    let cursor = await (await db()).transaction("written").store.openCursor();
    while (cursor) {
        total += cursor.value.bytes;
        cursor = await cursor.continue();
    }
    return total;
}

/** Called when a different destination folder is chosen. */
export async function clearWritten(): Promise<void> {
    await (await db()).clear("written");
}

/* Log ---------------------------------------------------------------------- */

/**
 * Support log. Never record subjects, filenames, addresses or content — the log
 * is written to disk and sent to support verbatim (§14).
 */
export async function appendLog(entry: Omit<LogRecord, "id" | "ts">): Promise<void> {
    await (await db()).add("log", { ...entry, ts: new Date().toISOString() });
}

export async function logForRun(runId: string): Promise<LogRecord[]> {
    return (await db()).getAllFromIndex("log", "by-run", runId);
}
